import { useMemo, useCallback, useState } from 'react';

import { Button, Icon, Typography, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { ApexOptions } from 'apexcharts';
import clsx from 'clsx';
import Chart from 'react-apexcharts';

import { SlideTransition } from 'components';
import { NoData } from 'components/DataTable/components';
import { EntityType } from 'enums';
import { FilterType, SchoolEffectResult } from 'generated';

import { useStyles } from './styles';

interface ScatterChartWrapperProps {
  data: SchoolEffectResult;
  segments?: FilterType[];
  value?: FilterType;
  onChangeSegment(segment: FilterType): void;
  loading?: boolean;
  entityType?: EntityType;
}

const colors = ['#666', '#0075B0', '#D582F9'];
const chartHeight = 400;
const chartWidth = 500;
const buttonDimension = 34;
const X_MIN = 40;
const X_MAX = 90;
const Y_MIN = 100;
const Y_MAX = 350;

export function ScatterChartWrapper({
  data,
  segments,
  value,
  onChangeSegment,
  loading,
  entityType,
}: ScatterChartWrapperProps): JSX.Element {
  const theme = useTheme();
  const classes = useStyles();

  const [direction, setDirection] = useState<'first' | 'second'>('first');

  const memoNotIsSchoolEntity = useMemo(() => {
    return entityType !== EntityType.SCHOOL;
  }, [entityType]);

  const memoIsCompanyEntity = useMemo(() => {
    return entityType === EntityType.COMPANY;
  }, [entityType]);

  const memoColors = useMemo(() => {
    if (!memoIsCompanyEntity) {
      return colors;
    }

    return colors.filter((_, index) => index !== 1);
  }, [memoIsCompanyEntity]);

  const memoMarkers = useMemo(() => {
    if (!memoIsCompanyEntity) {
      return [0, 5, 7];
    }

    return [0, 7];
  }, [memoIsCompanyEntity]);

  const memoHighlightChartLabel = useMemo(() => {
    const label = {
      [EntityType.BRAND]: 'Escolas da Marca',
      [EntityType.COMPANY]: 'Escolas da Rede',
      [EntityType.SCHOOL]: 'Sua Escola',
    };

    return label[entityType || EntityType.SCHOOL];
  }, [entityType]);

  const memoChartScale = useMemo(() => {
    if (!memoNotIsSchoolEntity) {
      return {
        X_MIN,
        X_MAX,
        Y_MIN,
        Y_MAX,
      };
    }

    return {
      X_MIN: 0,
      X_MAX: 100,
      Y_MIN: 0,
      Y_MAX: 500,
    };
  }, [memoNotIsSchoolEntity]);

  const memoFormattedData = useMemo(() => {
    const baseData = [
      {
        name: 'Proficiência esperada',
        type: 'line',
        data: [],
      },
      {
        name: 'Outras escolas',
        type: 'scatter',
        data: [],
      },
      {
        name: 'Sua escola',
        type: 'scatter',
        data: [],
      },
    ];

    if (!data) {
      if (memoIsCompanyEntity) {
        return baseData.filter((_, index) => index !== 1);
      }

      return baseData;
    }

    const internData = [
      {
        name: 'Proficiência esperada',
        type: 'line',
        data: data?.expectedProficiencyLine,
      },
      {
        name: 'Outras escolas',
        type: 'scatter',
        data: data?.regularPoints,
      },
      {
        name: memoHighlightChartLabel,
        type: 'scatter',
        data: data?.highlightPoints?.map(point => point.points),
      },
    ];

    if (memoIsCompanyEntity) {
      return internData.filter((_, index) => index !== 1);
    }

    return internData;
  }, [data, memoHighlightChartLabel, memoIsCompanyEntity]);

  const memoOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: !memoNotIsSchoolEntity ? 'scatter' : 'line',
        height: chartHeight,
        width: chartWidth,
        stacked: false,
        stackType: 'normal',
        zoom: {
          enabled: memoNotIsSchoolEntity,
          type: 'xy',
        },
        toolbar: {
          show: true,
          tools: {
            download:
              '<img src="/static/icons/apex-icon-download.svg" width="24" />',
            zoom: memoNotIsSchoolEntity && '<b></b>',
            zoomin:
              memoNotIsSchoolEntity &&
              '<img src="/static/icons/apex-icon-zoom-in.svg" width="24" />',
            zoomout:
              memoNotIsSchoolEntity &&
              '<img src="/static/icons/apex-icon-zoom-out.svg" width="24" />',
            pan: false,
            reset:
              memoNotIsSchoolEntity &&
              '<img src="/static/icons/apex-icon-refresh.svg" width="24" />',
          },
        },
        animations: {
          enabled: false,
        },
      },
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      stroke: {
        width: [2, 5, 7],
      },
      markers: {
        size: memoMarkers,
      },
      title: {
        text: 'Proficiência',
        align: 'left',
        offsetX: theme.spacing(1),
        style: {
          fontFamily: '"Public Sans Web", sans-serif',
          color: theme.palette.text.secondary,
          fontSize: '10px',
        },
      },
      colors: memoColors,
      legend: {
        fontFamily: '"Public Sans Web", sans-serif',
        show: true,
        showForSingleSeries: false,
        showForNullSeries: true,
        showForZeroSeries: true,
        height: 40,
        inverseOrder: true,
        onItemClick: {
          toggleDataSeries: entityType === EntityType.BRAND,
        },
        itemMargin: {
          horizontal: 10,
        },
      },
      tooltip: {
        enabled: memoNotIsSchoolEntity,
        intersect: memoNotIsSchoolEntity,
        shared: false,
        followCursor: true,
        marker: {
          show: false,
        },
        x: {
          show: false,
          formatter(_value, { seriesIndex, dataPointIndex } = {}) {
            if (
              (seriesIndex === 2 || memoIsCompanyEntity) &&
              data &&
              data?.highlightPoints &&
              data?.highlightPoints[dataPointIndex]
            ) {
              const { label } = data.highlightPoints[dataPointIndex] || {};

              if (label) {
                return `<b>${label}</b>`;
              }
            }
            return '<b>CONFIDENCIAL</b>';
          },
        },
        y: {
          title: {
            formatter() {
              return '';
            },
          },
          formatter(value, { seriesIndex, dataPointIndex }) {
            const tooltipData = memoFormattedData[seriesIndex];
            return `Proficiência: <b>${value}</b><br/>
            NSE: ${((tooltipData?.data as any) || [[]])[dataPointIndex][0]}`;
          },
        },
      },
      yaxis: {
        tickAmount: memoNotIsSchoolEntity ? 10 : 5,
        min: memoChartScale.Y_MIN,
        max: memoChartScale.Y_MAX,
      },
      xaxis: {
        type: 'numeric',
        tickAmount: memoNotIsSchoolEntity ? 10 : 5,
        min: memoChartScale.X_MIN,
        max: memoChartScale.X_MAX,
        title: {
          text: 'NSE',
          style: {
            fontFamily: '"Public Sans Web", sans-serif',
            fontSize: '10px',
          },
        },
        crosshairs: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
    }),
    [
      memoNotIsSchoolEntity,
      memoMarkers,
      theme,
      memoColors,
      entityType,
      memoChartScale.Y_MIN,
      memoChartScale.Y_MAX,
      memoChartScale.X_MIN,
      memoChartScale.X_MAX,
      memoIsCompanyEntity,
      data,
      memoFormattedData,
    ],
  );

  const memoPrevIsDisabled = useMemo(() => {
    if (!segments) {
      return true;
    }

    const currentSegmentIndex = segments.findIndex(
      segment => segment.id === value?.id,
    );

    return currentSegmentIndex === 0;
  }, [segments, value]);

  const memoNextIsDisabled = useMemo(() => {
    if (!segments) {
      return true;
    }

    const currentSegmentIndex = segments.findIndex(
      segment => segment.id === value?.id,
    );

    return currentSegmentIndex === segments.length - 1;
  }, [segments, value]);

  const memoNoDataForSegment = useMemo(() => {
    return (
      data &&
      !data?.highlightPoints?.length &&
      !!data?.regularPoints?.length &&
      !!segments?.length
    );
  }, [data, segments?.length]);

  const memoHasData = useMemo(() => {
    return (
      data &&
      !!data?.highlightPoints?.length &&
      !!data?.expectedProficiencyLine?.length
      // !!data?.regularPoints?.length
    );
  }, [data]);

  const handleChangeSegment = useCallback(
    (type: 'prev' | 'next') => () => {
      if (!segments) {
        return;
      }

      let newIndex = segments.findIndex(segment => segment.id === value?.id);

      newIndex = type === 'prev' ? newIndex - 1 : newIndex + 1;

      if (newIndex < 0 || newIndex > segments.length - 1) {
        return;
      }

      setDirection(type === 'prev' ? 'second' : 'first');

      onChangeSegment(segments[newIndex]);
    },
    [segments, value, onChangeSegment],
  );

  return (
    <div className={classes.root}>
      {!!segments?.length && (
        <div className={classes.segmentsContainer}>
          {loading ? (
            <Skeleton
              height={buttonDimension}
              width={buttonDimension}
              className={classes.skeleton}
            />
          ) : (
            <Button
              className={classes.segmentButton}
              disabled={memoPrevIsDisabled}
              size="small"
              color="primary"
              onClick={handleChangeSegment('prev')}
            >
              <Icon>chevron_left</Icon>
            </Button>
          )}
          {loading ? (
            <Skeleton
              height={buttonDimension}
              width={150}
              className={classes.skeleton}
            />
          ) : (
            <div className={classes.selectSegmentContainer}>
              <SlideTransition
                exitBeforeEnter={false}
                key={value?.id}
                direction={direction}
              >
                <Typography
                  className={classes.selectedSegment}
                  variant="subtitle2"
                >
                  {value?.label}
                </Typography>
              </SlideTransition>
            </div>
          )}

          {loading ? (
            <Skeleton
              height={buttonDimension}
              width={buttonDimension}
              className={classes.skeleton}
            />
          ) : (
            <Button
              disabled={memoNextIsDisabled}
              className={classes.segmentButton}
              size="small"
              color="primary"
              onClick={handleChangeSegment('next')}
            >
              <Icon>chevron_right</Icon>
            </Button>
          )}
        </div>
      )}
      {memoHasData && (
        <>
          {loading ? (
            <Skeleton
              className={clsx(classes.skeleton, classes.chartSkeleton)}
              width={chartWidth}
              height={chartHeight}
              animation="wave"
            />
          ) : (
            <Chart
              type="line"
              series={memoFormattedData}
              options={memoOptions}
              height={chartHeight}
              width={chartWidth}
            />
          )}
        </>
      )}
      {memoNoDataForSegment && (
        <NoData height={399} title="Não há dados" showSubtitle={false} />
      )}
    </div>
  );
}
